import * as React from 'react';
import {
  Connection as StardogConnection,
  dataSources as stardogDataSources,
} from 'stardog';
import { v4 as uuid } from 'uuid';
import { getStardogConnection, isEqual } from 'vet-bones/utils';
import { refetchConnectionToken } from 'vet-bones/utils/portal';

import { connectionSelector } from 'src/selectors/connection';
import {
  ProjectVirtualGraphField,
  ProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import { addLimitToSqlQuery } from 'src/utils/addLimitToSqlQuery';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

const sqlResultsObjectToArray = <T = any>(results: Record<string, T>) => {
  const indexes = Object.keys(results).map((key) => Number.parseInt(key));
  if (indexes.length === 0) {
    return [];
  }
  // The objects we get back from Stardog omit NULL values, along with
  // their keys, so we need to create an array with potentially empty slots
  const arrayResult = new Array(Math.max(...indexes));
  indexes.forEach((key) => {
    arrayResult[key] = results[key];
  });
  return arrayResult;
};

type DataSourceQueryResults = {
  mColumns: Record<
    string,
    {
      mName: string;
      mType: string;
      mSize: number;
    }
  >;
  mRows: Record<string, Record<string, string>>;
};

export type RunningQuery = {
  abortQuery: React.MutableRefObject<() => void>;
  allResultsIncluded: boolean;
  errors: Array<{ message: string }>;
  fields: ProjectVirtualGraphField[];
  isQuerying: boolean;
  rows: string[][];
  updateFieldLabel: (value: string, label: string) => void;
};

export const useProjectDataSourceQuery = ({
  dataSourceId,
  existingFields,
  existingQuery,
  forceRefresh,
  query,
  virtualGraphOptions,
}: {
  dataSourceId?: string;
  existingFields?: ProjectVirtualGraphField[];
  existingQuery?: string;
  forceRefresh?: boolean;
  query?: string;
  virtualGraphOptions?: ProjectVirtualGraphOptions;
}): RunningQuery => {
  const connection = useReduxSelector(connectionSelector);
  const [errors, setErrors] = React.useState<Array<{ message: string }>>([]);
  const [fields, setFields] = React.useState<ProjectVirtualGraphField[]>(
    existingFields || []
  );
  const [rows, setRows] = React.useState<string[][]>([]);
  const [allResultsIncluded, setAllResultsIncluded] = React.useState(true);

  const queryIds = React.useRef<Set<string>>(new Set());
  const [isQuerying, setIsQuerying] = React.useState(false);

  const didDataSourceIdChange = useDidValueChange(dataSourceId);
  const didQueryChange = useDidValueChange(query);
  const didVirtualGraphOptionsChange = useDidValueChange(
    virtualGraphOptions,
    isEqual
  );
  const needsRefresh =
    forceRefresh ||
    didDataSourceIdChange ||
    didQueryChange ||
    didVirtualGraphOptionsChange;

  const abortQuery = React.useRef<() => void>(() => {
    queryIds.current.clear();
    setErrors([]);
    setFields([]);
    setRows([]);
    setAllResultsIncluded(true);
    setIsQuerying(false);
  });

  const cleanup = (queryId: string) => {
    queryIds.current.delete(queryId);
    setIsQuerying(Boolean(queryIds.current.size));
  };

  const requestQuery = React.useCallback(
    (queryId: string, isKeepingHeaders?: boolean) => {
      if (dataSourceId && query && connection) {
        queryIds.current.add(queryId);
        setIsQuerying(true);
        const { limitedQuery, resultLimit } = addLimitToSqlQuery(query);
        const queryWithOptions = virtualGraphOptions
          ? { query: limitedQuery, options: virtualGraphOptions }
          : limitedQuery;

        const wrappedRequest = (stardogConn: StardogConnection) =>
          stardogDataSources.query(stardogConn, dataSourceId, queryWithOptions);

        wrappedRequest(getStardogConnection(connection))
          .then((response) =>
            response && response.status === 401
              ? refetchConnectionToken(connection.index).then((token) =>
                  token
                    ? wrappedRequest(
                        getStardogConnection({ ...connection, token })
                      )
                    : response
                )
              : response
          )
          .then((response) => {
            if (queryIds.current.has(queryId) && !response.ok) {
              const errorMessage =
                typeof response.body === 'string'
                  ? response.body
                  : response.body?.message ||
                    response.statusText ||
                    String(response.status);
              throw new Error(
                `${errorMessage} (${response.body?.code || response.status})`
              );
            }

            const results = response.body as DataSourceQueryResults;
            const newFields = sqlResultsObjectToArray(results.mColumns).map(
              ({ mName }: { mName: string }) => ({
                value: mName,
                label: mName,
              })
            );
            const newRows = sqlResultsObjectToArray(results.mRows).map((row) =>
              sqlResultsObjectToArray(row)
            );

            if (queryIds.current.has(queryId)) {
              setErrors([]);
              setFields(
                newFields.map((newField, index) => ({
                  ...newField,
                  label:
                    isKeepingHeaders && existingFields?.[index]?.label
                      ? existingFields[index].label
                      : newField.label,
                }))
              );
              setRows(newRows);
              setAllResultsIncluded(newRows.length < resultLimit);
            }
            cleanup(queryId);
          })
          .catch((err: Error) => {
            setErrors([
              { message: `Problem querying data source:\n${err.message}` },
            ]);
            setFields([]);
            setRows([]);
            setAllResultsIncluded(true);
            cleanup(queryId);
          });
      } else {
        // Only clear state if there's something to clear to avoid unnecessary re-renders
        setErrors((prev) => (prev.length > 0 ? [] : prev));
        setFields((prev) => (prev.length > 0 ? [] : prev));
        setRows((prev) => (prev.length > 0 ? [] : prev));
        setAllResultsIncluded((prev) => (prev === true ? prev : true));
        cleanup(queryId);
      }
    },
    [connection, dataSourceId, existingFields, query, virtualGraphOptions]
  );

  React.useEffect(() => {
    // do not request if recently connected instead of connection being changed
    if (!needsRefresh || !connection) {
      return;
    }

    const id = uuid();

    const shouldKeepHeaders =
      forceRefresh ||
      Boolean(existingFields && existingQuery && isEqual(existingQuery, query));

    requestQuery(id, shouldKeepHeaders);
  }, [
    connection,
    dataSourceId,
    existingFields,
    existingQuery,
    forceRefresh,
    needsRefresh,
    query,
    requestQuery,
    virtualGraphOptions,
  ]);

  React.useEffect(() => {
    const currentIds = queryIds.current;

    return () => {
      currentIds.forEach((queryId) => {
        cleanup(queryId);
      });
    };
  }, [dataSourceId, query]);

  const updateFieldLabel = React.useCallback(
    (value: string, label: string) => {
      setFields(
        fields.map((field) => ({
          ...field,
          label: field.value === value ? label : field.label,
        }))
      );
    },
    [fields]
  );

  return {
    abortQuery,
    allResultsIncluded,
    errors,
    fields,
    isQuerying,
    rows,
    updateFieldLabel,
  };
};
